// Models
import { IExerciseData } from 'storage/exercise/models'
import { IModelInput } from 'models'
import { IWorkoutRoutineState } from 'storage/workoutRoutine/models'
import IStore from 'lib/redux/models'

// React
import { FC, useCallback, useMemo } from 'react'

// Libraries
import { generatePath, useNavigate, useParams } from 'react-router-dom'
import { useFormContext } from 'react-hook-form'
import { useSelector } from 'react-redux'

// Misc
import { buttonClickTracking } from 'utils/tracking'
import { urls } from 'routes/paths'
import { useModal } from 'hooks'

// Components
import * as Styled from './styled'
import { Aligner, Button, FormButton, Table } from 'heeds-ds'
import {
  ContentBox,
  ExerciseRow,
  HeaderWorkoutRoutine,
  ModalConfirmation,
} from 'components'

// Constants
const COLUMNS = [
  { title: 'EXERCÍCIOS', width: '355px' },
  { title: 'SÉRIE', width: '80px' },
  { title: 'DESCANSO', width: '80px' },
  { title: 'REPETIÇÃO', width: '80px' },
  { title: 'CARGA', width: '80px' },
  { title: '' },
]

type Props = {
  openExerciseGifModal: (exercise: IExerciseData) => void
  removeWorkoutSet?: (setId: string) => void
  workoutData?: IModelInput[]
}

const Desktop: FC<Props> = ({
  openExerciseGifModal,
  removeWorkoutSet,
  workoutData,
}) => {
  const { id = '' } = useParams()
  const { openModal, isVisible } = useModal()
  const { workoutRoutine } = useSelector<IStore, IWorkoutRoutineState>(
    (state) => state.workoutRoutine,
  )
  const {
    formState: { isDirty },
  } = useFormContext()
  const navigate = useNavigate()

  const handleGoBack = useCallback(() => {
    if (isDirty) return openModal('exercise-go-back-confirmation')
    return navigate(-1)
  }, [isDirty, navigate, openModal])

  const renderComments = useMemo(
    () =>
      workoutRoutine?.comments && (
        <ContentBox title="Comentários sobre a Rotina">
          {workoutRoutine?.comments?.split('\n')?.map((row, index) => {
            return (
              <Styled.Comment key={index}>
                {row} <br />
              </Styled.Comment>
            )
          })}
        </ContentBox>
      ),
    [workoutRoutine?.comments],
  )

  const renderWorkoutData = useMemo(
    () =>
      workoutData?.map((workout, index) => (
        <ContentBox
          key={index}
          collapsible
          startsOpen={index === 0}
          title={workout.name.toUpperCase()}
        >
          <Table
            columns={COLUMNS}
            pageSize={6}
            listLength={1}
            data={Object.values(workout.workout_set)}
            renderRow={(workoutSet) => (
              <ExerciseRow
                key={workoutSet.id}
                onClickImage={openExerciseGifModal}
                prefix={`models.${index}`}
                removeWorkoutSet={removeWorkoutSet}
                workoutSet={workoutSet}
              />
            )}
          />
        </ContentBox>
      )),
    [openExerciseGifModal, removeWorkoutSet, workoutData],
  )

  const handleCancelClick = useCallback(() => {
    navigate(generatePath(urls.athleteRoutines, { id }), {
      replace: true,
    })
  }, [navigate, id])

  return (
    <Aligner gap="24px" flex="column">
      <HeaderWorkoutRoutine />

      {renderComments}

      <Aligner flex="column" gap="24px">
        {renderWorkoutData}
      </Aligner>

      <Styled.ButtonContainer>
        <Button
          cancel
          onClick={handleCancelClick}
          track={buttonClickTracking}
          trackName="navigate_to_athlete_routines"
          variation="outlined"
        >
          Cancelar
        </Button>

        <Styled.ButtonContainerRight>
          <Button
            onClick={handleGoBack}
            track={buttonClickTracking}
            trackName="go_back_in_navigation"
            variation="outlined"
          >
            Voltar
          </Button>

          <FormButton
            track={buttonClickTracking}
            trackName="create_or_update_models"
          >
            Salvar
          </FormButton>
        </Styled.ButtonContainerRight>
      </Styled.ButtonContainer>

      {isVisible === 'exercise-go-back-confirmation' && (
        <ModalConfirmation
          onConfirm={() => navigate(-1)}
          title="Confirmação"
          confirmTitle="Confirmar"
          description="Tem certeza que deseja voltar? Voltando agora você vai perder as alterações realizadas."
        />
      )}
    </Aligner>
  )
}

export default Desktop
